import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {FormattedMessage} from "react-intl";
import {bindActionCreators} from "redux";
import styled from "styled-components";
import {
    getCartItems,
    getisCartReviewed,
    getIsCartInitialised,
    getIsCartDocked,
    getActiveCartTab,
    getisCartCompleted,
    getLocale,
    getIsMobile,
    getRoute,
} from "rootReducer";
import {URLS, MODAL_LOCATION} from "containers/constants";
import {Modal, Button} from "components";
import {returnToCart, setActiveCartTab, closeCart, setCartDocked} from "../actionCreators";
import {CartDetailsContainer, AddressContainer, PaymentContainer, ReviewContainer, OrderCompleteContainer} from "./";
import {CartHeader} from "./components/CartHeader";
import * as appActions from "../App/actions";
import messages from "./messages";

/* CART STYLES */
const CartWindow = styled.section`
    flex-direction: row;
    display: flex;
    position: absolute;
    top: 0;
    align-items: flex-start;
    margin-top: 0;
    height: 100%;
    width: 100%;
    margin-left: 0;
    z-index: 999999999;
    ${(props) => (props.toggleMobile ? "justify-content: flex-end;" : "")}
    ${getCartStyles}
`;

const getCartStyles = (props) => {
    if (props.toggleMobile) {
        if (props.showCart) {
            return props.cartDocked ? getMobileCartDockedStyles() : getMobileCartUndockedStyles();
        }
        return getMobileCartHiddenStyles();
    }
    return props.showCart ? getDesktopCartStyles() : getDesktopCartHiddenStyles();
};

const getMobileCartDockedStyles = () => `
    transform: translateX(calc(100% - 52px));
    transition: transform 1s ease-out;
    z-index: 2000;
    width: 100%;
`;

const getMobileCartUndockedStyles = () => `
    z-index: 2000;
    transform: translateX(calc(0% + 52px));
    transition: transform 1s ease-out;
    width: calc(100% - 52px);
`;

const getMobileCartHiddenStyles = () => `
    transform: translateX(calc(100%));
    transition: transform 1s ease-out;
`;

const getDesktopCartStyles = () => `
    transform: translateX(100%);
    transition: transform 1s ease-out;
`;

const getDesktopCartHiddenStyles = () => `
    transform: translateX(0);
    transition: transform 1s ease-out;
`;

const CartHolder = styled.div`
    display: flex;
    align-items: flex-start;
    background-color: #fff;
    flex-direction: column;
    width: ${(props) => (props.toggleMobile ? "calc(100% - 52px)" : "100%")};
    height: 100%;
    border-top-right-radius: 2px;
    box-shadow: rgba(0, 0, 0, 0.2) -3px 0px 15px;
`;
const CartBody = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    height: calc(100% - 98px);
`;
const TogglerGenesys = styled.div`
    margin: 52px 0 0;
    ${(props) => (props.toggleMobile ? "display: block;" : "display: none;")}
`;

const Router = ({route, updateRoute}) => {
    let component;
    switch (route.sub) {
        case URLS.DETAILS:
            component = <CartDetailsContainer />;
            break;
        case URLS.ADDRESS:
            component = <AddressContainer />;
            break;
        case URLS.PAYMENT:
            component = <PaymentContainer updateRoute={updateRoute} />;
            break;
        case URLS.REVIEW:
            component = <ReviewContainer updateRoute={updateRoute} />;
            break;
        case URLS.COMPLETE:
            component = <OrderCompleteContainer />;
            break;
        default:
            component = <CartDetailsContainer />;
    }
    return component;
};

/* Matches current URL against URL constants to return the correct id */
const getTabKeyBasedOnRoute = (route) => {
    const {DETAILS, ADDRESS, PAYMENT, REVIEW} = URLS;

    switch (route) {
        case DETAILS:
            return messages.detailsLabel.id;
        case ADDRESS:
            return messages.addressLabel.id;
        case PAYMENT:
            return messages.paymentLabel.id;
        case REVIEW:
            return messages.reviewLabel.id;
        default:
            return "";
    }
};
/* Matches current tabKeyId against the messages ids and returns the correct route */
const getRouteBasedOnTabKey = (tabKey) => {
    const {DETAILS, ADDRESS, PAYMENT, REVIEW} = URLS;

    switch (tabKey) {
        case messages.detailsLabel.id:
            return DETAILS;
        case messages.addressLabel.id:
            return ADDRESS;
        case messages.paymentLabel.id:
            return PAYMENT;
        case messages.reviewLabel.id:
            return REVIEW;
        default:
            return "";
    }
};

const CartContainer = (props) => {
    const {
        isCartReviewed,
        isCartDocked,
        isCartInitialised,
        cartItems,
        activeCartTab,
        returnToCartHandler,
        setActiveCartTabHandler,
        closeCartHandler,
        setCartDockedHandler,
        isCartCompleted,
        modal,
        route,
        locale,
        isMobile,
        appActions: {retryActionDispatcher, updateRoute},
    } = props;
    const cartTabKey = getTabKeyBasedOnRoute(route.sub);
    if (cartTabKey) setActiveCartTabHandler(cartTabKey);
    const ErrorButton = modal.isShown ? (
        <Button
            text={<FormattedMessage id={modal.cta1} />}
            onClick={retryActionDispatcher}
            classes="btn btn-filled-bby"
        />
    ) : null;

    const handleCartClose = () => {
        closeCartHandler();
    };
    const handleClick = () => {
        if (isCartDocked) {
            window.parent.postMessage({type: "toggleExpandedCartOff"}, "*");
        } else {
            window.parent.postMessage({type: "toggleExpandedCartOn"}, "*");
        }
        setCartDockedHandler(!isCartDocked);
    };

    useEffect(() => {
        window.parent.postMessage({type: "cartInitialised"}, "*");
        window.parent.postMessage({type: "covertChatInitialized"}, "*");
        if (isMobile) {
            setCartDockedHandler(true);
            window.parent.postMessage({type: "toggleExpandedCartOn"}, "*");
        } else {
            setCartDockedHandler(false);
            window.parent.postMessage({type: "toggleExpandedCartOff"}, "*");
        }
    }, [isMobile]);

    const togglerImage =
        locale === "en"
            ? `${process.env.SF_HOST_ENV_VAR.trim()}/images/tab_bby_en.svg`
            : `${process.env.SF_HOST_ENV_VAR.trim()}/images/tab_bby_fr.svg`;

    return (
        <CartWindow
            className="cart-window"
            showCart={isCartInitialised}
            cartDocked={isCartDocked}
            toggleMobile={isMobile}
        >
            {modal.isShown && modal.location === MODAL_LOCATION.CART ? (
                <Modal
                    show={modal.isShown}
                    message={<FormattedMessage id={modal.message} />}
                    element={ErrorButton}
                    placeholder={<FormattedMessage id={modal.placeholder} />}
                />
            ) : null}
            <CartHolder toggleMobile={isMobile}>
                <CartHeader
                    isCartReviewed={isCartReviewed}
                    isCartCompleted={isCartCompleted}
                    cartItems={cartItems.length}
                    closeCartHandler={handleCartClose}
                    returnToCartHandler={returnToCartHandler}
                    activeCartTab={activeCartTab}
                    getRouteBasedOnTabKey={getRouteBasedOnTabKey}
                    updateRoute={updateRoute}
                    locale={locale}
                />
                <CartBody className="cart-body">
                    <Router route={route} updateRoute={updateRoute} />
                </CartBody>
            </CartHolder>
            <TogglerGenesys onClick={handleClick} toggleMobile={isMobile}>
                <img alt="Toggler" src={togglerImage} />
            </TogglerGenesys>
        </CartWindow>
    );
};
CartContainer.propTypes = {
    isCartReviewed: PropTypes.bool,
    isCartInitialised: PropTypes.bool,
    isCartCompleted: PropTypes.bool,
    isCartDocked: PropTypes.bool,
    cartItems: PropTypes.array,
    activeCartTab: PropTypes.string,
    returnToCartHandler: PropTypes.func,
    closeCartHandler: PropTypes.func,
    setCartDockedHandler: PropTypes.func,
    locale: PropTypes.string,
    isMobile: PropTypes.bool,
    route: PropTypes.object,
    setActiveCartTabHandler: PropTypes.func,
    modal: PropTypes.object,
    appActions: PropTypes.shape({
        retryActionDispatcher: PropTypes.func,
        updateRoute: PropTypes.func,
    }),
};

const mapDispatchToProps = (dispatch) => ({
    returnToCartHandler: bindActionCreators(returnToCart, dispatch),
    setActiveCartTabHandler: bindActionCreators(setActiveCartTab, dispatch),
    closeCartHandler: bindActionCreators(closeCart, dispatch),
    setCartDockedHandler: bindActionCreators(setCartDocked, dispatch),
    appActions: bindActionCreators(
        {
            retryActionDispatcher: appActions.retryActionDispatcher,
            updateRoute: appActions.updateRoute,
        },
        dispatch,
    ),
});

const mapStateToProps = (state) => ({
    cartItems: getCartItems(state),
    isCartInitialised: getIsCartInitialised(state),
    isCartDocked: getIsCartDocked(state),
    isCartReviewed: getisCartReviewed(state),
    isCartCompleted: getisCartCompleted(state),
    activeCartTab: getActiveCartTab(state),
    modal: state.app.modal,
    locale: getLocale(state),
    isMobile: getIsMobile(state),
    route: getRoute(state),
});

export const Cart = connect(mapStateToProps, mapDispatchToProps)(CartContainer);



// WEBPACK FOOTER //
// ./src/containers/Cart/Cart.js